import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowUpRight, Play } from 'lucide-react';
import { useState, useRef } from 'react';
import { api, cfmt, pfmt } from '../lib/api';
import SEOHead from '../components/common/SEOHead';
import NotFound from './NotFound';

/**
 * Dedicated SEO landing page — /lifestyle/:slug
 *
 * Editorial photography hero → ambient video block → three market insights
 * → recommended neighbourhoods grid → Strategy Session CTA.
 */
export default function LifestylePage() {
    const { slug } = useParams();
    const { data, isLoading, isError } = useQuery({
        queryKey: ['lifestyle', slug],
        queryFn: async () => (await api.get(`/lifestyle/${slug}`)).data,
        retry: false,
    });
    const [videoPlaying, setVideoPlaying] = useState(false);
    const videoRef = useRef(null);

    if (isLoading) return <div className="min-h-[60vh]" />;
    if (isError || !data) return <NotFound />;

    const playVideo = () => {
        setVideoPlaying(true);
        setTimeout(() => { videoRef.current?.play?.(); }, 50);
    };

    return (
        <div className="bg-white">
            <SEOHead
                title={`${data.name} — Southern Ontario Neighbourhoods`}
                description={data.intro?.slice(0, 160) || data.tagline}
                path={`/lifestyle/${slug}`}
                image={data.hero_image}
            />

            {/* ==================== HERO ==================== */}
            <section className="relative w-full bg-black" style={{ minHeight: 'min(80vh, 720px)' }}>
                <img
                    src={data.hero_image}
                    alt={data.name}
                    className="absolute inset-0 w-full h-full object-cover opacity-90"
                    data-testid={`lifestyle-hero-${slug}`}
                />
                <div
                    className="absolute inset-0"
                    style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.65) 100%)' }}
                />
                <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pt-32 pb-16 lg:pb-24 min-h-[inherit] flex flex-col justify-end" style={{ minHeight: 'min(80vh, 720px)' }}>
                    <div className="flex items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-white/70 font-mono-data">
                        <span className="inline-block w-6 h-px bg-white/60" />
                        Explore by Lifestyle · {data.total_matches} neighbourhoods
                    </div>
                    <h1
                        className="font-editorial text-white mt-6 tracking-editorial"
                        style={{ fontSize: 'clamp(48px, 7vw, 104px)', lineHeight: 1.02, letterSpacing: '-0.03em' }}
                    >
                        {data.name}
                    </h1>
                    <p className="mt-6 font-editorial italic text-white/85 max-w-2xl" style={{ fontSize: 'clamp(18px, 1.5vw, 24px)', lineHeight: 1.45 }}>
                        {data.tagline}
                    </p>
                </div>
            </section>

            {/* ==================== INTRO PROSE ==================== */}
            <section className="border-b border-neutral-200">
                <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-60px' }}
                        transition={{ duration: 0.7 }}
                        className="font-editorial text-black leading-relaxed"
                        style={{ fontSize: 'clamp(20px, 1.6vw, 26px)', letterSpacing: '-0.005em' }}
                        data-testid="lifestyle-intro"
                    >
                        {data.intro}
                    </motion.p>
                </div>
            </section>

            {/* ==================== AMBIENT VIDEO ==================== */}
            {data.video_url && (
                <section className="border-b border-neutral-200 bg-black">
                    <div className="max-w-[1400px] mx-auto">
                        <div className="relative w-full bg-black" style={{ aspectRatio: '16 / 9' }} data-testid="lifestyle-video-block">
                            {videoPlaying ? (
                                <video
                                    ref={videoRef}
                                    src={data.video_url}
                                    controls
                                    autoPlay
                                    playsInline
                                    className="absolute inset-0 w-full h-full object-cover bg-black"
                                />
                            ) : (
                                <>
                                    <video
                                        src={data.video_url}
                                        muted
                                        loop
                                        autoPlay
                                        playsInline
                                        className="absolute inset-0 w-full h-full object-cover opacity-70"
                                        aria-hidden="true"
                                    />
                                    <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.55) 100%)' }} />
                                    <button
                                        onClick={playVideo}
                                        data-testid="lifestyle-video-play"
                                        className="absolute inset-0 flex flex-col items-center justify-center text-white group"
                                    >
                                        <div className="w-20 h-20 border border-white/70 flex items-center justify-center group-hover:bg-white group-hover:text-black transition-colors">
                                            <Play className="w-8 h-8 ml-1" />
                                        </div>
                                        <div className="mt-6 font-mono-data text-[11px] tracking-[0.24em] uppercase">
                                            Ambient reel · {data.name}
                                        </div>
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </section>
            )}

            {/* ==================== MARKET INSIGHTS ==================== */}
            <section className="border-b border-neutral-200 bg-[#FAFAF8]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
                    <div className="max-w-3xl">
                        <div className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data">
                            Market insights · What the data says
                        </div>
                        <h2 className="font-editorial mt-4 leading-[1.05]" style={{ fontSize: 'clamp(32px, 4vw, 52px)', letterSpacing: '-0.02em' }}>
                            The evidence behind this category.
                        </h2>
                    </div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t border-neutral-200">
                        {data.market_insights.map((insight, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-60px' }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                                className="border-r border-b border-neutral-200 bg-white p-8 lg:p-10"
                                data-testid={`lifestyle-insight-${i}`}
                            >
                                <div className="font-mono-data text-[12px] tabular text-neutral-400">
                                    Insight {String(i + 1).padStart(2, '0')}
                                </div>
                                <p className="mt-6 font-editorial text-black leading-snug" style={{ fontSize: '19px', letterSpacing: '-0.005em' }}>
                                    {insight}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== RECOMMENDED NEIGHBOURHOODS ==================== */}
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
                    <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
                        <div>
                            <div className="overline">The shortlist</div>
                            <h2 className="font-editorial mt-4 leading-[1.05]" style={{ fontSize: 'clamp(32px, 4vw, 52px)', letterSpacing: '-0.02em' }}>
                                {data.total_matches} neighbourhoods that fit.
                            </h2>
                        </div>
                        <Link to="/neighbourhoods" className="hidden md:inline-flex items-center gap-2 text-[13.5px] link-editorial">
                            All neighbourhoods <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>

                    {data.recommendations.length === 0 ? (
                        <div className="border border-neutral-200 p-12 text-center text-neutral-500">
                            No neighbourhood matches this category yet — check back as our coverage expands.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-l border-t border-neutral-200">
                            {data.recommendations.map((n, i) => (
                                <Link
                                    key={n.slug}
                                    to={`/neighbourhoods/${n.slug}`}
                                    data-testid={`lifestyle-rec-${n.slug}`}
                                    className="group border-r border-b border-neutral-200 bg-white hover:bg-brand-navy hover:text-white transition-colors duration-500"
                                >
                                    <div className="relative overflow-hidden bg-neutral-200" style={{ aspectRatio: '4 / 3' }}>
                                        {n.hero_image && (
                                            <img src={n.hero_image} alt={n.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                                        )}
                                        <div className="absolute top-3 left-3 bg-brand-navy text-white px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase font-mono-data">
                                            {String(i + 1).padStart(2, '0')}
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <div className="font-editorial text-[22px] tracking-editorial leading-tight group-hover:italic">
                                            {n.name}
                                        </div>
                                        <div className="mt-1.5 text-[11px] tracking-[0.18em] uppercase text-neutral-500 group-hover:text-white/60 font-mono-data">
                                            {n.city} · {n.region}
                                        </div>
                                        {n.tagline && (
                                            <p className="mt-4 text-[13px] leading-relaxed text-neutral-600 group-hover:text-white/80 line-clamp-2">
                                                {n.tagline}
                                            </p>
                                        )}
                                        <div className="mt-5 pt-4 border-t border-neutral-200 group-hover:border-white/20 flex items-center justify-between font-mono-data text-[11.5px] text-neutral-500 group-hover:text-white/70">
                                            <span>{cfmt(n.median_price)}</span>
                                            <span className="text-[#2F6B47] group-hover:text-emerald-300">+{pfmt(n.forecast_5yr_pct)} 5Y</span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* ==================== STRATEGY SESSION CTA ==================== */}
            <section className="bg-brand-navy text-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7">
                        <div className="text-[11px] tracking-[0.24em] uppercase text-white/60 font-mono-data flex items-center gap-3">
                            <span className="inline-block w-6 h-px bg-white/60" />
                            Book a Strategy Session
                        </div>
                        <h2 className="font-editorial mt-6 leading-[1.03] text-white" style={{ fontSize: 'clamp(36px, 4.8vw, 68px)', letterSpacing: '-0.02em' }}>
                            Turn the shortlist into a decision.
                        </h2>
                        <p className="mt-6 font-editorial text-white/80 max-w-xl" style={{ fontSize: 'clamp(16px, 1.15vw, 19px)', lineHeight: 1.55 }}>
                            Thirty minutes with an independent analyst. We&apos;ll walk your top three
                            neighbourhoods, model the affordability, and answer the question your friends
                            can&apos;t: is this actually the right move — for you?
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex items-end">
                        <Link
                            to="/strategy-session"
                            data-testid="lifestyle-strategy-cta"
                            className="inline-flex items-center gap-2 bg-white text-black px-8 py-4 text-[14px] font-medium hover:bg-neutral-200"
                            style={{ letterSpacing: '0.02em' }}
                        >
                            Book a Strategy Session
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
